import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Paper, Chip, IconButton, Tab, Tabs, ToggleButtonGroup, ToggleButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SortIcon from '@mui/icons-material/Sort';
import Draggable from 'react-draggable';
import HudVirtualizedJsonTree from './JsonTree/HudVirtualizedJsonTree';
import DiffHistoryList from './DiffHistoryList';
import useHudPanel from './hooks/useHudPanel';
import useResizablePanel from './hooks/useResizablePanel';
import useSplitPanel from './hooks/useSplitPanel';
import AddNodeInline from './AddNodeInline';
import { MAX_JSON_FILE_SIZE_BYTES } from './JsonTree/constants';
import DebouncedSearchInput from '../../components/reusable/DebouncedSearchInput';
import { DEFAULT_SEARCH_DEBOUNCE_MS } from '../../components/reusable/DebouncedSearchInput/constants';

const HudView = ({
  title,
  searchTerm,
  onSearchChange,
  onReload,
  treeData,
  controlVariables = {},
  onEdit,
  supportsTouch,
  recentDiffs,
  loadingRecentDiffs,
  onAddProperty,
  activeInlineAddPath,
  onInlineAddPathChange,
  onAddNode,
  priorityMap = {},
  exactMatchOnly = false,
  onExactMatchToggle,
  onFileLoaded,
  onClearFileLoaded,
  hasFileLoaded = false
}) => {
  const hudRef = useRef(null);
  const pillRef = useRef(null);
  const fileInputRef = useRef(null);
  const { hudCollapsed, hudWrapperStyles, hudPosition, hudDragHandlers, collapseHud, pillPosition, pillHandlers } = useHudPanel({ initialCollapsed: false });
  const { panelSize, startResizing } = useResizablePanel({ initialWidth: 1280, initialHeight: 1024, minWidth: 360, minHeight: 360 });
  const { ratio: mainRatio, startResizing: startSplitResize, attachContainer } = useSplitPanel({ initialRatio: 0.7, minRatio: 0.4, maxRatio: 0.85 });
  const [inlineAddVisible, setInlineAddVisible] = useState(false);
  const [fileLoadError, setFileLoadError] = useState('');
  const [sortTopLevelBySize, setSortTopLevelBySize] = useState(false);
  const [showAllPropertiesOfMatchingNodes, setShowAllPropertiesOfMatchingNodes] = useState(true);
  const [activeControl, setActiveControl] = useState('all');

  const { handleDrag: handleHudDrag, handleStop: handleHudStop } = hudDragHandlers;
  const { handleStart: handlePillStart, handleDrag: handlePillDrag, handleStop: handlePillStop, handleClick: handlePillClick, handleKeyDown: handlePillKeyDown } = pillHandlers;

  const gapPx = 8;
  const splitterWidth = 8;
  const inspectorWidth = Math.max(panelSize.width * (1 - mainRatio) - splitterWidth - gapPx, 160);
  const treeWidth = Math.max(panelSize.width - inspectorWidth - splitterWidth - gapPx, 250);
  const topLevelKeys = useMemo(() => {
    if (!treeData || typeof treeData !== 'object' || Array.isArray(treeData)) return [];
    return Object.keys(treeData);
  }, [treeData]);
  const controlIds = useMemo(() => Object.keys(controlVariables).sort(), [controlVariables]);
  const selectedControlKeys = useMemo(() => new Set(activeControl === 'all' ? [] : controlVariables[activeControl] || []), [activeControl, controlVariables]);
  const visibleTreeData = useMemo(() => {
    if (activeControl === 'all' || !treeData || typeof treeData !== 'object' || Array.isArray(treeData)) return treeData;
    return Object.fromEntries(Object.entries(treeData).filter(([key]) => selectedControlKeys.has(key)));
  }, [activeControl, selectedControlKeys, treeData]);
  useEffect(() => {
    if (activeControl !== 'all' && !controlIds.includes(activeControl)) setActiveControl('all');
  }, [activeControl, controlIds]);

  const handleToggleInlineAdd = () => {
    setInlineAddVisible((prev) => !prev);
  };

  const handleFileChange = useCallback(
    (event) => {
      setFileLoadError('');
      const file = event.target.files?.[0];
      if (!file) return;
      if (file.size > MAX_JSON_FILE_SIZE_BYTES) {
        setFileLoadError('File too large (max 10 MB)');
        event.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result);
          if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            setFileLoadError('JSON root must be an object');
            return;
          }
          onFileLoaded?.(parsed);
        } catch {
          setFileLoadError('Invalid JSON');
        }
      };
      reader.onerror = () => setFileLoadError('Failed to read file');
      reader.readAsText(file);
      event.target.value = '';
    },
    [onFileLoaded]
  );

  const handleLoadFromFileClick = useCallback(() => {
    setFileLoadError('');
    fileInputRef.current?.click();
  }, []);

  return (
    <>
      <Draggable nodeRef={hudRef} handle=".lsv-hud__drag-handle" cancel=".lsv-hud__no-drag" position={hudPosition} onDrag={handleHudDrag} onStop={handleHudStop}>
        <Box
          ref={hudRef}
          sx={{
            ...hudWrapperStyles,
            width: panelSize.width
          }}>
          <Paper
            sx={{
              width: panelSize.width,
              height: panelSize.height || 520,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              background: 'linear-gradient(160deg, rgba(6,21,29,0.92) 0%, rgba(10,32,40,0.92) 45%, rgba(12,40,46,0.92) 100%)',
              border: '1px solid rgba(0,255,170,0.18)',
              borderRadius: 2,
              boxShadow: '0 25px 45px rgba(0,0,0,0.45)',
              color: '#f8ffff',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden',
              position: 'relative'
            }}>
            <input ref={fileInputRef} type="file" accept=".json,application/json" onChange={handleFileChange} style={{ display: 'none' }} aria-hidden="true" />
            <Box className="lsv-hud__drag-handle" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.4, flexGrow: 1 }}>
                {title}
              </Typography>
              <Chip
                label={hasFileLoaded ? 'From file' : 'Local snapshot'}
                size="small"
                sx={{
                  bgcolor: 'rgba(0,255,170,0.15)',
                  color: '#00ffaa',
                  fontWeight: 600,
                  textTransform: 'uppercase'
                }}
              />
              <Button
                className="lsv-hud__no-drag"
                size="small"
                variant="outlined"
                startIcon={<SortIcon fontSize="small" />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSortTopLevelBySize((prev) => !prev);
                }}
                sx={{
                  minWidth: 0,
                  border: sortTopLevelBySize ? '1px solid #00ffaa' : '1px solid rgba(0,255,170,0.35)',
                  color: sortTopLevelBySize ? '#04231f' : '#00ffaa',
                  backgroundColor: sortTopLevelBySize ? '#00ffaa' : 'transparent',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1.25,
                  py: 0.25,
                  boxShadow: sortTopLevelBySize ? '0 0 0 1px rgba(0,255,170,0.65), 0 0 14px rgba(0,255,170,0.45)' : 'none',
                  '&:hover': {
                    border: '1px solid #00ffaa',
                    backgroundColor: sortTopLevelBySize ? '#2dffbf' : 'rgba(0,255,170,0.08)'
                  }
                }}>
                Size
              </Button>
              <IconButton
                className="lsv-hud__no-drag"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  collapseHud();
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                sx={{ color: '#7fffd4' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box className="lsv-hud__no-drag" sx={{ px: 2, pt: 0.5 }}>
              <Tabs
                value={activeControl}
                onChange={(_, value) => setActiveControl(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Control variables"
                sx={{
                  minHeight: 34,
                  '& .MuiTab-root': { minHeight: 34, py: 0.5, color: '#d1fffa', textTransform: 'none' },
                  '& .Mui-selected': { color: '#00ffaa' },
                  '& .MuiTabs-indicator': { backgroundColor: '#00ffaa' }
                }}>
                <Tab value="all" label="All" />
                {controlIds.map((controlId) => (
                  <Tab key={controlId} value={controlId} label={controlId} />
                ))}
              </Tabs>
            </Box>
            <Box className="lsv-hud__no-drag" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5 }}>
              <Box sx={{ height: 34, display: 'flex', alignItems: 'center', gap: 1 }}>
                {onAddNode && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleInlineAdd();
                    }}
                    sx={{
                      borderRadius: 1.5,
                      border: '1px solid rgba(0,255,170,0.6)',
                      color: '#00ffaa',
                      backgroundColor: 'rgba(0,255,170,0.12)',
                      '&:hover': {
                        backgroundColor: 'rgba(0,255,170,0.2)'
                      }
                    }}
                    aria-label="Add node">
                    <AddIcon fontSize="small" />
                  </IconButton>
                )}
                <DebouncedSearchInput
                  value={searchTerm}
                  onDebouncedChange={(value) => onSearchChange({ target: { value } })}
                  debounceMs={DEFAULT_SEARCH_DEBOUNCE_MS}
                  placeholder="Search keys & values..."
                  ariaLabel="Search JSON"
                  wrapperSx={{ flex: 1, minWidth: 0 }}
                  textFieldSx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(9, 30, 38, 0.92)',
                      boxShadow: 'inset 0 0 0 1px rgba(0,255,170,0.12)',
                      '& input': {
                        color: '#e7fbf8'
                      },
                      '& fieldset, & .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,255,170,0.28)'
                      },
                      '&:hover fieldset': {
                        borderColor: '#00ffaa'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00ffaa',
                        borderWidth: 1.5
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      color: '#f4fdfc'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'rgba(255,255,255,0.65)',
                      opacity: 1
                    }
                  }}
                  spinnerSx={{ color: '#00ffaa' }}
                />
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={exactMatchOnly ? 'exact' : 'contains'}
                  onChange={(_, next) => {
                    if (!next) return;
                    onExactMatchToggle?.(next === 'exact');
                  }}
                  sx={{
                    ml: 1,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      px: 1.25,
                      width: 80,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: 'rgba(0,255,170,0.3)',
                      color: '#d1fffa'
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      borderColor: '#00ffaa',
                      backgroundColor: 'rgba(0,255,170,0.18)',
                      color: '#ffffff'
                    }
                  }}>
                  <ToggleButton value="contains">Contains</ToggleButton>
                  <ToggleButton value="exact">Exact</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={showAllPropertiesOfMatchingNodes ? 'node-context' : 'matches-only'}
                  onChange={(_, next) => {
                    if (!next) return;
                    setShowAllPropertiesOfMatchingNodes(next === 'node-context');
                  }}
                  sx={{
                    ml: 1,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      px: 1.25,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: 'rgba(0,255,170,0.3)',
                      color: '#d1fffa'
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      borderColor: '#00ffaa',
                      backgroundColor: 'rgba(0,255,170,0.18)',
                      color: '#ffffff'
                    }
                  }}>
                  <ToggleButton value="matches-only">Matches only</ToggleButton>
                  <ToggleButton value="node-context">Show all</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              {onFileLoaded && (
                <Button
                  onClick={handleLoadFromFileClick}
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon fontSize="small" />}
                  sx={{
                    flexShrink: 0,
                    border: '1px solid rgba(0,255,170,0.35)',
                    color: '#00ffaa',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    px: 1.75,
                    backdropFilter: 'blur(2px)',
                    '&:hover': { border: '1px solid #00ffaa', backgroundColor: 'rgba(0,255,170,0.08)' },
                    '&:focus-visible': { border: '1px solid #00ffaa' },
                    '&:active': { border: '1px solid #00ffaa' }
                  }}>
                  Load from file
                </Button>
              )}
              {hasFileLoaded ? (
                <Button
                  onClick={onClearFileLoaded}
                  size="small"
                  variant="outlined"
                  sx={{
                    flexShrink: 0,
                    border: '1px solid rgba(0,255,170,0.35)',
                    color: '#00ffaa',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    px: 1.75,
                    backdropFilter: 'blur(2px)',
                    '&:hover': { border: '1px solid #00ffaa', backgroundColor: 'rgba(0,255,170,0.08)' },
                    '&:focus-visible': { border: '1px solid #00ffaa' },
                    '&:active': { border: '1px solid #00ffaa' }
                  }}>
                  Use live status
                </Button>
              ) : (
                <Button
                  onClick={onReload}
                  size="small"
                  variant="outlined"
                  sx={{
                    flexShrink: 0,
                    border: '1px solid rgba(0,255,170,0.35)',
                    color: '#00ffaa',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    px: 1.75,
                    backdropFilter: 'blur(2px)',
                    '&:hover': { border: '1px solid #00ffaa', backgroundColor: 'rgba(0,255,170,0.08)' },
                    '&:focus-visible': { border: '1px solid #00ffaa' },
                    '&:active': { border: '1px solid #00ffaa' }
                  }}>
                  Load Live Status
                </Button>
              )}
            </Box>
            {fileLoadError && (
              <Box className="lsv-hud__no-drag" sx={{ px: 2, pt: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#ff7043' }}>
                  {fileLoadError}
                </Typography>
              </Box>
            )}
            {onAddNode && inlineAddVisible && (
              <Box className="lsv-hud__no-drag" sx={{ px: 2, pt: 0.5 }}>
                <AddNodeInline visible variant="hud" onCancel={() => setInlineAddVisible(false)} onCreate={onAddNode} existingKeys={topLevelKeys} />
              </Box>
            )}
            <Box ref={attachContainer} className="lsv-hud__no-drag" sx={{ position: 'relative', display: 'flex', gap: `${gapPx}px`, flexGrow: 1, px: 2, pb: 2, mt: 1.5, minHeight: 0 }}>
              <Box
                sx={{
                  width: treeWidth,
                  height: '100%',
                  minHeight: 0,
                  overflowX: 'hidden',
                  pr: 1,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.08)', borderRadius: 4 },
                  '&::-webkit-scrollbar-thumb': { background: 'linear-gradient(180deg,#22ffaa33 0%,#00ffaa66 100%)', borderRadius: 4 }
                }}>
                <HudVirtualizedJsonTree
                  data={visibleTreeData}
                  searchTerm={searchTerm}
                  onEdit={onEdit}
                  supportsTouch={supportsTouch}
                  onAddProperty={onAddProperty}
                  activeInlineAddPath={activeInlineAddPath}
                  onInlineAddPathChange={onInlineAddPathChange}
                  priorityMap={priorityMap}
                  exactMatchOnly={exactMatchOnly}
                  sortTopLevelBySize={sortTopLevelBySize}
                  showAllPropertiesOfMatchingNodes={showAllPropertiesOfMatchingNodes}
                />
              </Box>
              <Box
                className="lsv-hud__no-drag"
                onPointerDown={startSplitResize}
                sx={{
                  width: 8,
                  cursor: 'col-resize',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  borderRadius: 3,
                  alignSelf: 'stretch',
                  flexShrink: 0,
                  touchAction: 'none'
                }}
              />
              <Box className="lsv-hud__no-drag" sx={{ width: inspectorWidth, minWidth: 140, flexShrink: 0 }}>
                <DiffHistoryList entries={recentDiffs} loading={loadingRecentDiffs} sortBySize={sortTopLevelBySize} />
              </Box>
            </Box>
            {/* Resize handles */}
            <Box
              className="lsv-hud__no-drag"
              onPointerDown={(e) => startResizing(e, 'right')}
              sx={{
                position: 'absolute',
                top: 0,
                right: -6,
                width: 12,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 25,
                touchAction: 'none'
              }}
            />
            <Box
              className="lsv-hud__no-drag"
              onPointerDown={(e) => startResizing(e, 'left')}
              sx={{
                position: 'absolute',
                top: 0,
                left: -6,
                width: 12,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 25,
                touchAction: 'none'
              }}
            />
            <Box
              className="lsv-hud__no-drag"
              onPointerDown={(e) => startResizing(e, 'top')}
              sx={{
                position: 'absolute',
                top: -6,
                left: 0,
                width: '100%',
                height: 12,
                cursor: 'row-resize',
                zIndex: 25,
                touchAction: 'none'
              }}
            />
            <Box
              className="lsv-hud__no-drag"
              onPointerDown={(e) => startResizing(e, 'bottom')}
              sx={{
                position: 'absolute',
                bottom: -6,
                left: 0,
                width: '100%',
                height: 12,
                cursor: 'row-resize',
                zIndex: 25,
                touchAction: 'none'
              }}
            />
            <Box
              className="lsv-hud__no-drag"
              onPointerDown={(e) => startResizing(e, 'right-bottom')}
              sx={{
                position: 'absolute',
                bottom: -8,
                right: -8,
                width: 18,
                height: 18,
                cursor: 'nwse-resize',
                zIndex: 26,
                touchAction: 'none'
              }}
            />
          </Paper>
        </Box>
      </Draggable>

      {hudCollapsed && (
        <Draggable nodeRef={pillRef} position={pillPosition} onStart={handlePillStart} onDrag={handlePillDrag} onStop={handlePillStop}>
          <Paper
            ref={pillRef}
            className="lsv-hud__pill"
            elevation={8}
            role="button"
            tabIndex={0}
            onKeyDown={handlePillKeyDown}
            onClick={(e) => {
              e.stopPropagation();
              handlePillClick();
            }}
            sx={{
              position: 'fixed',
              top: 88,
              right: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 999,
              backgroundColor: 'rgba(6,21,29,0.92)',
              border: '1px solid rgba(0,255,170,0.3)',
              color: '#00ffaa',
              zIndex: (theme) => theme.zIndex.modal + 19,
              boxShadow: '0 18px 36px rgba(0,0,0,0.45)',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Expand Debug HUD
            </Typography>
            <IconButton size="small" sx={{ color: '#00ffaa', pointerEvents: 'none' }}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Paper>
        </Draggable>
      )}
    </>
  );
};

HudView.propTypes = {
  title: PropTypes.string.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  treeData: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.oneOf([null])]),
  controlVariables: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  onEdit: PropTypes.func.isRequired,
  supportsTouch: PropTypes.bool,
  loadingRecentDiffs: PropTypes.bool,
  recentDiffs: PropTypes.arrayOf(
    PropTypes.shape({
      tick: PropTypes.number.isRequired,
      diff: PropTypes.object.isRequired
    })
  ),
  onAddProperty: PropTypes.func,
  activeInlineAddPath: PropTypes.string,
  onInlineAddPathChange: PropTypes.func,
  onAddNode: PropTypes.func,
  priorityMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  exactMatchOnly: PropTypes.bool,
  onExactMatchToggle: PropTypes.func,
  onFileLoaded: PropTypes.func,
  onClearFileLoaded: PropTypes.func,
  hasFileLoaded: PropTypes.bool
};

export default HudView;
